/**
 * Medcor Transport settings (M38-T06/T07, ADR-0066). amtc_admin only; the
 * server's `transport_config:write` capability is the real guard. Amounts are
 * integer pesewas; `null` means "not set" (a fare) or "closed" (a capacity).
 */
import { apiFetch } from './client'

const BASE = '/api/v1/admin/transport-config'

export type TransportService =
  | 'standard_ride'
  | 'pts'
  | 'car_hire'
  | 'executive'
export type OnlineService = Exclude<TransportService, 'executive'>

export const ONLINE_SERVICES: readonly OnlineService[] = [
  'standard_ride',
  'pts',
  'car_hire',
]

export const SERVICE_NAMES: Record<TransportService, string> = {
  standard_ride: 'Standard Ride',
  pts: 'Patient Transport',
  car_hire: 'Car Hire with Driver',
  executive: 'Executive',
}

export type Fare = {
  id: string
  service: TransportService | 'all'
  item: string
  zone: number | null
  amount_pesewas: number | null
  updated_at: string
}

export type Area = {
  id: string
  name: string
  zone: number
  travel_minutes: number
  active: boolean
}

export type AreaChange = Partial<Omit<Area, 'id'>>

export type HospitalZone = {
  hospital_id: string
  name: string
  zone: number | null
}

export type CapacityOverride = {
  service: OnlineService
  date: string
  max_bookings: number
}

export type Capacity = {
  defaults: Record<OnlineService, number | null>
  overrides: CapacityOverride[]
}

export type Setting = {
  key: string
  value: number
  minimum: number
  maximum: number
}

export type MissingFare = { service: string; item: string; zone: number | null }

export type ServiceStatus = {
  bookable: boolean
  phone_only: boolean
  missing_fares: MissingFare[]
  missing_capacity: boolean
}

export type Status = Record<TransportService, ServiceStatus>

/** How often the safety check stopped a booking, the last 30 days (M39-T08).
 * Days with no stops have no row. */
export type GateStops = {
  total: number
  days: { day: string; stops: number }[]
}

export const TRANSPORT_CONFIG_KEY = ['admin', 'transport-config'] as const

type Token = string | null

async function get<T>(token: Token, path: string): Promise<T> {
  return (await apiFetch<{ data: T }>(BASE + path, { token })).data
}

async function send<T>(
  token: Token,
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  return (await apiFetch<{ data: T }>(BASE + path, { token, method, body }))
    .data
}

export const fetchStatus = (t: Token) => get<Status>(t, '/status')
export const fetchFares = (t: Token) => get<Fare[]>(t, '/fares')
export const fetchAreas = (t: Token) => get<Area[]>(t, '/areas')
export const fetchHospitalZones = (t: Token) =>
  get<HospitalZone[]>(t, '/hospital-zones')
export const fetchCapacity = (t: Token) => get<Capacity>(t, '/capacity')
export const fetchSettings = (t: Token) => get<Setting[]>(t, '/settings')
export const fetchGateStops = (t: Token) => get<GateStops>(t, '/gate-stops')

export const setFare = (t: Token, id: string, amount: number | null) =>
  send<Fare>(t, 'PUT', `/fares/${encodeURIComponent(id)}`, {
    amount_pesewas: amount,
  })

export const createArea = (t: Token, area: Omit<Area, 'id' | 'active'>) =>
  send<Area>(t, 'POST', '/areas', area)

export const updateArea = (t: Token, id: string, change: AreaChange) =>
  send<Area>(t, 'PATCH', `/areas/${encodeURIComponent(id)}`, change)

export const setHospitalZone = (
  t: Token,
  hospitalId: string,
  zone: number | null,
) =>
  send<HospitalZone>(
    t,
    'PUT',
    `/hospital-zones/${encodeURIComponent(hospitalId)}`,
    { zone },
  )

export const setDefaultCapacity = (
  t: Token,
  service: OnlineService,
  max: number | null,
) =>
  send<Capacity>(t, 'PUT', `/capacity/${service}/default`, {
    max_bookings: max,
  })

export const setDatedCapacity = (
  t: Token,
  service: OnlineService,
  date: string,
  max: number,
) =>
  send<Capacity>(t, 'PUT', `/capacity/${service}/dates/${date}`, {
    max_bookings: max,
  })

export const removeDatedCapacity = (
  t: Token,
  service: OnlineService,
  date: string,
) => send<Capacity>(t, 'DELETE', `/capacity/${service}/dates/${date}`)

export const setSetting = (t: Token, key: string, value: number) =>
  send<Setting>(t, 'PUT', `/settings/${encodeURIComponent(key)}`, { value })
